import { h, render } from 'vue'
import type { Component, VNode, VNodeArrayChildren } from 'vue'

type RawChildren =
  | string
  | number
  | boolean
  | VNode
  | VNodeArrayChildren
  | (() => RawChildren)

/**
 * Creates an HTMLElement from a Vue component.
 *
 * It handles passing various child content types to the component that our HTMLElement is created from,
 * including functions, strings, and other Vue components.
 *
 * @param component Vue component
 * @param props Props to pass to the component
 * @param children Children/slot content
 * @returns HTMLElement
 *
 * @example
 * ```ts
 * // Basic component
 * const element = createHTMLElement(MyComponent, { prop: 'value' })
 *
 * // Component with string content
 * // delay is passed to xToolTip component as prop
 * // 'Hello World' is passed as children/slot content
 * const element = createHTMLElement(xToolTip, { delay: 0 }, 'Hello World')
 *
 * // Component with function children (reactive content)
 * const element = createHTMLElement(
 *   xToolTip,
 *   { trigger: 'always', delay: 0 },
 *   () => tooltipContent.value
 * )
 *
 * // Component with Vue components as children
 * const element = createHTMLElement(Container, {}, MyChildComponent)
 * ```
 */
export function createHTMLElement<
  T extends Component,
  P extends Record<string, unknown> = Record<string, unknown>,
>(component: T, props?: P, children?: RawChildren): HTMLElement {
  const container = document.createElement('div')
  const vnode = h(component, props || {}, children)

  render(vnode, container)

  const htmlElement = container.firstElementChild as HTMLElement
  if (!htmlElement) {
    throw new Error(
      `Failed to create element from component: ${component.name || 'Unknown Component'}`
    )
  }

  return htmlElement
}
